/**
 * ServiceMaster.js – Histopathology Service Master (simplified from DepartmentMaster).
 * Module type fixed to Histopathology. Only: service head, service head code, service code,
 * service name, service charge, unit_type, unit, edit unit/amount/discount checkboxes.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Tooltip,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExportContainer, GridToolbarQuickFilter, GridCsvExportMenuItem } from "@mui/x-data-grid";
import { ExpandMore, Delete as DeleteIcon } from "@mui/icons-material";
import { Colors } from "../../Styles/Colors";
import { CommonEditButton, CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { CurrencySymbol } from "../../Utility/Constants";
import CommonValidation from "../../Utility/CommonValidation";
import Loader from "../../Components/Loader";
import "../../Styles/Configuration.css";

const MODULE_TYPE = "HISTOPATHOLOGY";

const INPUT_SX = {
  '& .MuiInputBase-root': { height: '2.25vw' },
  '& .MuiInputBase-input': { fontSize: '0.85vw', padding: '0.3vw 0.6vw' },
  '& .MuiSelect-select': { fontSize: '0.85vw', padding: '0.3vw 0.6vw' },
  '& .MuiInputLabel-root': { fontSize: '0.85vw', top: '-0.3vw', marginTop: '0.1vw' },
  '& .MuiInputLabel-shrink': { top: '0.05vw' },
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.65vw' },
  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
  '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { fontSize: '1.1vw' },
};

export default function ServiceMaster() {
  const [departmentData, setDepartmentData] = useState([]);
  const [originalList, setOriginalList] = useState({});
  const [headerList, setHeaderList] = useState([]);
  const [filterHead, setFilterHead] = useState(null);
  const [filterName, setFilterName] = useState(null);
  const [filteredServiceList, setFilteredServiceList] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [formState, setFormState] = useState({
    serviceHeadCode: null,
    serviceHead: null,
    serviceCode: null,
    serviceName: null,
    serviceHeadId: null,
    serviceNameId: null,
    serviceHeadCodeData: [],
    serviceHeadData: [],
    serviceCodeData: [],
    serviceNameData: [],
    serviceSearchHeadCode: "",
    serviceSearchHead: "",
    serviceSearchCode: "",
    serviceSearchName: "",
    serviceCharge: "",
    selectedUnitValue: 1,
    selectedDaysCount: "",
    selectedUnit: "Nos",
    selectedTime: null,
    intervalTime: null,
    intervalAmount: 0,
  });
  const [editUnit, setEditUnit] = useState(true);
  const [editAmount, setEditAmount] = useState(true);
  const [editDiscount, setEditDiscount] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [EditID, setEditID] = useState(null);

  const [enableOthers, setEnableOthers] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [errorMsgText, setErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [specimenList, setSpecimenList] = useState([]);
  const [selectedSpecimen, setSelectedSpecimen] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const errorMessage = (msg) => {
    setErrorMsgText(msg);
    setErrorMsg(true);
  };
  const successMessage = (msg) => {
    setSuccessMsgText(msg);
    setSuccessMsg(true);
  };
  const msgClose = () => {
    setErrorMsg(false);
    setSuccessMsg(false);
  };

  const handleDeleteByHead = () => {
    if (deleteRow?.service_head_id == null) return;
    setIsLoader(true);
    RestAPIService.delete(Serviceurls.HISTOPATHOLOGY_SERVICE_CONFIG, {
      delete_by: "head",
      id: [deleteRow.service_head_id],
    })
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res.data.message ?? "Deleted successfully");
          getDepartmentData();
          setDeleteDialogOpen(false);
          setDeleteRow(null);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  };

  const handleDeleteByService = () => {
    if (deleteRow?.service_charge_id == null) return;
    setIsLoader(true);
    RestAPIService.delete(Serviceurls.HISTOPATHOLOGY_SERVICE_CONFIG, {
      delete_by: "service",
      id: [deleteRow.service_charge_id],
    })
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res.data.message ?? "Deleted successfully");
          getDepartmentData();
          setDeleteDialogOpen(false);
          setDeleteRow(null);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  };

  const getSpecimenList = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SPECIMEN)
      .then((res) => {
        if (res?.data?.status === "success") {
          const data = res?.data?.data ?? [];
          const active = (Array.isArray(data) ? data : []).filter((item) => item.is_active);
          setSpecimenList(active);
        }
      })
      .catch((e) => errorMessage(e?.response?.data?.message || e?.message));
  }, []);

  const getDepartmentData = useCallback(() => {
    setIsLoader(true);
    const params = new URLSearchParams({
      module_type: MODULE_TYPE,
      service_head_id: "",
      service_details_id: "",
      service_head: filterHead?.head_name ? encodeURIComponent(filterHead.head_name) : "",
      service_name: filterName?.service_name ? encodeURIComponent(filterName.service_name) : "",
      service_head_code: "",
      charge_type: "Normal",
    });
    RestAPIService.getAll(Serviceurls.NEW_DEPARTMENT_MASTER + "?" + params.toString())
      .then((res) => {
        if (res?.data?.status === "success") {
          const raw = res.data.data || [];
          const mapped = raw.map((row) => {
            const headDetails = row.service_head_details ?? "";
            const serviceHead = headDetails.includes(" - ") ? headDetails.split(" - ").slice(1).join(" - ").trim() : headDetails;
            return {
              ...row,
              service_head: row.service_head ?? serviceHead,
              specimen_type: row.specimen_type ?? row.specimen ?? "",
            };
          });
          setDepartmentData(mapped);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  }, [filterHead, filterName]);

  useEffect(() => {
    getDepartmentData();
  }, [getDepartmentData]);

  useEffect(() => {
    getSpecimenList();
  }, [getSpecimenList]);

  // Derive headerList, serviceList, originalList from departmentData (no MODULE_LIST)
  useEffect(() => {
    if (!departmentData?.length) {
      setHeaderList([]);
      setServiceList([]);
      setFilteredServiceList([]);
      setOriginalList({});
      return;
    }
    const byHead = {};
    departmentData.forEach((row) => {
      const code = row.service_head_code ?? "";
      if (!byHead[code]) {
        byHead[code] = { head_name: row.service_head ?? "", service_head_code: code, service_details: [] };
      }
      const details = byHead[code].service_details;
      const has = details.some((s) => (s.service_code ?? s.service_details_id) === (row.service_code ?? row.service_details_id));
      if (!has) {
        details.push({
          service_code: row.service_code,
          service_name: row.service_name,
          service_details_id: row.service_details_id,
          unit_type: row.unit_type,
          unit: row.unit,
          head_name: row.service_head,
          service_head_code: row.service_head_code,
        });
      }
    });
    const histo = Object.values(byHead);
    setOriginalList({ [MODULE_TYPE]: histo });
    setHeaderList(histo);
    const serviceDetailsArray = histo.flatMap((item) =>
      (item?.service_details || []).map((s) => ({
        ...s,
        head_name: item?.head_name,
        service_head_code: item?.service_head_code,
      }))
    );
    setServiceList(serviceDetailsArray);
    setFilteredServiceList(serviceDetailsArray);
  }, [departmentData]);

  const clearFunction = (emptyType) => {
    setFormState({
      serviceHeadCode: null,
      serviceHead: null,
      serviceCode: null,
      serviceName: null,
      serviceHeadId: null,
      serviceNameId: null,
      serviceHeadCodeData: headerList,
      serviceHeadData: headerList,
      serviceCodeData: [],
      serviceNameData: [],
      serviceSearchHeadCode: "",
      serviceSearchHead: "",
      serviceSearchCode: "",
      serviceSearchName: "",
      serviceCharge: "",
      selectedUnitValue: 1,
      selectedDaysCount: "",
      selectedUnit: "Nos",
      selectedTime: null,
      intervalTime: null,
      intervalAmount: 0,
    });
    setEditUnit(true);
    setEditAmount(true);
    setEditDiscount(true);
    setEditID(null);
    setEditMode(false);
    setFilterHead(null);
    setFilterName(null);
    setSelectedSpecimen(null);
    if (emptyType === "head") {

      setEnableOthers(false);
    } else if (emptyType === "service") {
      setEnableOthers(false);
    }
  };

  const onChangeAuto = (e, val, name) => {
    let updatedState = { ...formState };
    switch (name) {
      case "serviceSearchHeadCode":
        if (!val) {
          clearFunction("head");
          break;
        }

        updatedState = {
          ...updatedState,
          serviceHead: val,
          serviceHeadCode: val,
          serviceCodeData: val?.service_details || [],
          serviceNameData: val?.service_details || [],
        };
        break;
      case "serviceSearchHead":
        if (!val) {
          clearFunction("head");
          break;
        }

        updatedState = {
          ...updatedState,
          serviceHead: val,
          serviceHeadCode: typeof val === "object" ? val : null,
          serviceCodeData: val?.service_details || [],
          serviceNameData: val?.service_details || [],
          serviceHeadId: null,
          serviceNameId: null,
          serviceCode: null,
          serviceName: null,
        };
        break;
      case "serviceSearchCode":
        if (!val) {
          setEnableOthers(false);
          updatedState = { ...updatedState, serviceCode: null, serviceName: null, serviceNameId: null, [name]: "" };
          break;
        }
        setEnableOthers(true);
        updatedState = { ...updatedState, serviceCode: val, serviceName: val, serviceNameId: null, [name]: "" };
        break;
      case "serviceSearchName":
        if (!val) {
          setEnableOthers(false);
          updatedState = { ...updatedState, serviceCode: null, serviceName: null, serviceNameId: null, [name]: "" };
          break;
        }
        setEnableOthers(true);
        updatedState = {
          ...updatedState,
          serviceCode: typeof val === "object" ? val : null,
          serviceName: val,
          selectedUnit: val?.id ? val?.unit_type || "Nos" : formState.selectedUnit,
          selectedTime: val?.id && val?.unit_type === "Hrs" ? val?.unit : formState.selectedTime,
          serviceNameId: null,
          [name]: "",
        };
        break;
      default:
        updatedState = { ...updatedState, [name]: val };
    }
    setFormState(updatedState);
  };

  const textBox = (label, name, val, width, disable = false) => (
    <TextField
      size="small"
      name={name}
      disabled={disable}
      value={val !== "" && val !== undefined ? val : ""}
      sx={{ width, marginRight: "0.3vw", ...INPUT_SX }}
      label={label}
      onChange={(e) => {
        const value = e.target.value;
        if (name === "serviceCharge" && value !== "" && !CommonValidation.DecimalNumber(value)) return;
        setFormState((prev) => ({ ...prev, [name]: value }));
      }}
      variant="outlined"
    />
  );

  const autoComplete = (label, name, val, list, width, disabled = false) => (
    <Box sx={{ width, marginRight: "0.3vw", ...INPUT_SX }}>
      <Autocomplete
        freeSolo={name.startsWith("serviceSearch") && !disabled}
        clearOnBlur={false}
        size="small"
        options={list || []}
        disabled={disabled}
        getOptionLabel={(option) => {
          if (option === null || option === undefined) return "";
          if (typeof option === "string") return option;
          if (label === "Service Head Code") return option?.service_head_code ?? "";
          if (label === "Service Head*") return option?.head_name ?? "";
          if (label === "Service Code") return option?.service_code ?? "";
          if (label === "Service Name*") return option?.service_name ?? "";
          return "";
        }}
        value={val ?? null}
        onChange={(event, newValue) => onChangeAuto(event, newValue, name)}
        onInputChange={(e, newInputValue, reason) => {
          if (disabled) return;
          if (reason !== "input") return;
          const val = newInputValue || "";
          const trimmed = val;
          if (name === "serviceSearchHeadCode") {
            setFormState((prev) => ({ ...prev, serviceSearchHeadCode: val, serviceHeadCode: trimmed ? { service_head_code: trimmed } : null, serviceHead: null, serviceCodeData: [], serviceNameData: [] }));
          }
          if (name === "serviceSearchHead") {
            setFormState((prev) => ({ ...prev, serviceSearchHead: val, serviceHead: trimmed ? { head_name: trimmed } : null, serviceCodeData: [], serviceNameData: [], serviceCode: null, serviceName: null }));
          }
          if (name === "serviceSearchCode") {
            setFormState((prev) => ({ ...prev, serviceSearchCode: val, serviceCode: trimmed ? { service_code: trimmed } : null, serviceName: null }));
            if (trimmed) setEnableOthers(true);
          }
          if (name === "serviceSearchName") {
            setFormState((prev) => ({ ...prev, serviceSearchName: val, serviceName: trimmed ? { service_name: trimmed } : null, serviceCode: null }));
            if (trimmed) setEnableOthers(true);
          }
        }}
        renderInput={(params) => <TextField {...params} label={label} variant="outlined" size="small" />}
      />
    </Box>
  );

  const addDepartmentDetails = () => {
    const isUpdate = editMode && EditID?.service_charge_id;

    if (isUpdate) {
      const chargeNum = formState.serviceCharge ? parseFloat(formState.serviceCharge, 10) : 0;
      const serviceCharge = chargeNum.toFixed(2);
      const payload = {
        service_charge_id: EditID.service_charge_id,
        service_head_id: EditID.service_head_id,
        service_details_id: EditID.service_details_id,
        service_head: EditID.service_head.trim() ?? "",
        service_name: EditID.service_name.trim() ?? "",
        specimen_id: EditID.specimen_id ?? selectedSpecimen?.id ?? 1,
        service_charge: serviceCharge,
        edit_unit: editUnit,
        edit_amount: editAmount,
        edit_discount: editDiscount,
        active_by_service_head: true,
        active_by_service_name: true,
        is_active: true,
      };
      setIsLoader(true);
      RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_SERVICE_CONFIG)
        .then((res) => {
          if (res?.data?.status === "success") {
            successMessage(res.data.message);
            getDepartmentData();
            clearFunction();
          }
          setIsLoader(false);
        })
        .catch((e) => {
          setIsLoader(false);
          errorMessage(e?.response?.data?.message || e?.message);
        });
      return;
    }

    const serviceHeadLabel = formState.serviceHead?.head_name ?? formState.serviceSearchHead ?? "";
    const serviceNameVal = formState.serviceName?.service_name ?? (formState.serviceName && typeof formState.serviceName === "string" ? formState.serviceName : formState.serviceSearchName ?? "");

    if (!serviceHeadLabel && !(formState.serviceHeadCode?.service_head_code ?? formState.serviceSearchHeadCode ?? "")) {
      return errorMessage("Select or enter Service Head");
    }
    if (!serviceNameVal && !(formState.serviceCode?.service_code ?? formState.serviceSearchCode ?? "")) {
      return errorMessage("Select or enter Service Name");
    }
    if (!selectedSpecimen || !selectedSpecimen.id) {
      return errorMessage("Select Specimen");
    }

    const serviceHead = (typeof formState.serviceHead === "object" && formState.serviceHead?.head_name)
      ? formState.serviceHead.head_name.trim()
      : String(serviceHeadLabel).trim();
    const serviceName = typeof formState.serviceName === "object" && formState.serviceName?.service_name
      ? formState.serviceName.service_name.trim()
      : String(serviceNameVal).trim();
    const chargeNum = formState.serviceCharge ? parseFloat(formState.serviceCharge, 10) : 0;
    const serviceCharge = chargeNum.toFixed(2);

    const payload = {
      service_head: serviceHead,
      service_name: serviceName,
      specimen_id: selectedSpecimen?.id || 1,
      service_charge: serviceCharge,
      active_by_service_head: true,
      active_by_service_name: true,
      is_active: true,
      edit_unit: editUnit,
      edit_amount: editAmount,
      edit_discount: editDiscount,
      module_type: MODULE_TYPE,
    };

    setIsLoader(true);
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_SERVICE_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res.data.message);
          getDepartmentData();
          clearFunction();
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  };

  departmentData.forEach((el, i) => (el.sno = i + 1));

  const columns = [
    { field: "s_no", headerName: "S.No", flex: 0.05, sortable: false, renderCell: (params) => params.row.sno },
    {
      field: "service_head",
      headerName: "Service Head Details",
      flex: 0.2,
      renderCell: (params) => {
        const s = (params.row?.service_head_code ? params.row.service_head_code + " - " : "") + (params.row?.service_head || "");
        return s.length > 25 ? <Tooltip title={s}><Typography fontSize="0.8dvw">{s.slice(0, 25) + "..."}</Typography></Tooltip> : (s || "-");
      },
    },
    {
      field: "service_name",
      headerName: "Service Name Details",
      flex: 0.2,
      renderCell: (params) => {
        const s = (params.row?.service_code ? params.row.service_code + " - " : "") + (params.row?.service_name || "");
        return s.length > 25 ? <Tooltip title={s}><Typography fontSize="0.8dvw">{s.slice(0, 25) + "..."}</Typography></Tooltip> : (s || "-");
      },
    },
    {
      field: "specimen",
      headerName: "Specimen",
      flex: 0.15,
      renderCell: (params) => {
        const row = params.row;
        if (row?.specimen_type) return <Box component="span">{row.specimen_type}</Box>;
        if (row?.specimen_id && specimenList?.length) {
          const spec = specimenList.find((s) => s.id === row.specimen_id);
          return <Box component="span">{spec?.specimen_type ?? spec?.specimen_code ?? "-"}</Box>;
        }
        return <Box component="span">-</Box>;
      },
    },
    {
      field: "service_charge",
      headerName: "Service Charge",
      flex: 0.1,
      renderCell: (params) => (params?.row?.service_charge != null ? `${CurrencySymbol} ${CommonValidation.formatAmount(params.row.service_charge)}` : "0.00"),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.08,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.2vw" }}>
          <CommonEditButton
            size="1.5vw"
            onClick={() => {
              const data = params.row;
              setFormState({
                ...formState,
                serviceHeadCode: data?.service_head_code ?? "",
                serviceHead: data?.service_head ?? "",
                serviceCode: data?.service_code ?? "",
                serviceName: data?.service_name ?? "",
                serviceCharge: data?.service_charge,
              });
              setSelectedSpecimen(data?.specimen_id ? specimenList.find((s) => s.id === data.specimen_id) ?? null : null);
              setEditUnit(!!data?.edit_unit);
              setEditAmount(!!data?.edit_amount);
              setEditDiscount(!!data?.edit_discount);
              setEditID(data);
              setEditMode(true);

              setEnableOthers(true);
              setIsAccordionOpen(true);
            }}
          />
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setDeleteRow(params.row);
                setDeleteDialogOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box component="div" sx={{ height: "100%", width: "100%", backgroundColor: Colors.Background, padding: "0.5vw", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
      <Accordion expanded={isAccordionOpen} onChange={() => setIsAccordionOpen((p) => !p)}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ maxHeight: "3vh", "& .MuiAccordionSummary-content": { margin: 1 } }}>
          <Typography variant="body2" fontWeight="bold">Service Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0, marginTop: "-2.5vh" }}>
          <Box component="div" sx={{ width: "100%", backgroundColor: "white", padding: "0.6vw", boxSizing: "border-box" }}>
            <Box component="div" sx={{ display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: "0.5vw" }}>
              {autoComplete("Service Head Code", "serviceSearchHeadCode", formState.serviceHeadCode ?? formState.serviceSearchHeadCode, formState.serviceHeadCodeData.length ? formState.serviceHeadCodeData : headerList, "12vw", true)}
              {autoComplete("Service Head*", "serviceSearchHead", formState.serviceHead ?? formState.serviceSearchHead, formState.serviceHeadData.length ? formState.serviceHeadData : headerList, "12vw", editMode)}
              {autoComplete("Service Code", "serviceSearchCode", formState.serviceCode ?? formState.serviceSearchCode, formState.serviceCodeData, "12vw", true)}
              {autoComplete("Service Name*", "serviceSearchName", formState.serviceName ?? formState.serviceSearchName, formState.serviceNameData, "12vw", editMode)}
              <Box sx={{ width: "12vw", marginRight: "0.3vw", ...INPUT_SX }}>
                <Autocomplete
                  size="small"
                  options={specimenList}
                  value={selectedSpecimen}
                  onChange={(e, val) => setSelectedSpecimen(val)}
                  getOptionLabel={(opt) => (opt?.specimen_type ?? opt?.specimen_code ?? "").toString()}
                  renderInput={(params) => <TextField {...params} label="Specimen *" variant="outlined" size="small" />}
                  disabled={editMode}
                />
              </Box>
              {textBox("Service Charge*", "serviceCharge", formState.serviceCharge, "10vw", !enableOthers && !editMode)}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "1vw", marginTop: "1vw" }}>
              <FormControlLabel control={<Checkbox checked={editUnit} onChange={(e) => setEditUnit(e.target.checked)} size="small" disabled={!enableOthers && !editMode} />} label="Edit Unit" />
              <FormControlLabel control={<Checkbox checked={editAmount} onChange={(e) => setEditAmount(e.target.checked)} size="small" disabled={!enableOthers && !editMode} />} label="Edit Amount" />
              <FormControlLabel control={<Checkbox checked={editDiscount} onChange={(e) => setEditDiscount(e.target.checked)} size="small" disabled={!enableOthers && !editMode} />} label="Edit Discount" />
              <Button size="small" variant="outlined" sx={{ textTransform: "capitalize" }} onClick={() => clearFunction()}>Clear</Button>
              <Button size="small" variant="contained" sx={{ textTransform: "capitalize" }} onClick={addDepartmentDetails}>{editMode ? "Update" : "Add"}</Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ width: "100%", flex: 1, minHeight: 0, marginTop: "0.5vw", backgroundColor: "white", boxSizing: "border-box", overflow: "hidden" }}>
        <DataGrid
          sx={{ width: "100%" }}
          rows={departmentData}
          columns={columns}
          getRowId={(row) => row.sno}
          rowHeight={40}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>No Records</Box>
            ),
          }}
          showToolbar
          localeText={{ toolbarColumns: "", toolbarDensity: "", toolbarExport: "", toolbarFilters: "", toolbarExportPrint: "" }}
          loading={isLoader}
        />
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setDeleteRow(null); }} aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">Delete Service</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete? Choose delete option:
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeleteRow(null); }} color="inherit">Cancel</Button>
          <Button onClick={handleDeleteByHead} color="error" variant="outlined">Delete by Head</Button>
          <Button onClick={handleDeleteByService} color="error" variant="contained">Delete by Service</Button>
        </DialogActions>
      </Dialog>

      {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
